import { useCallback, useEffect, useState } from "react";
import { getDecomposition } from "../../../services/clearing/station-performance";
import type { DecompositionReport, ReportingOptions } from "../../../types/stationPerformance";

interface UseDecompositionArgs {
  from: string;
  to: string;
  options: ReportingOptions;
  enabled?: boolean;
}

/**
 * The money-change decomposition for a month pair.
 *
 * A failure here is not surfaced as an error: the decomposition explains a change, and if it cannot be
 * computed the rest of the variance screen is still perfectly usable. It simply does not render.
 */
export const useDecomposition = ({ from, to, options, enabled = true }: UseDecompositionArgs) => {
  const [report, setReport] = useState<DecompositionReport | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchDecomposition = useCallback(async () => {
    if (!enabled || !from || !to) {
      setReport(null);
      return;
    }
    setLoading(true);
    // The decomposition counts units entering and leaving, which a station filter would make
    // meaningless: one station cannot dilute itself.
    const result = await getDecomposition({ from, to, options: { ...options, station: null, provider: null } });
    setReport(result?.success ? (result.data ?? null) : null);
    setLoading(false);
  }, [from, to, options, enabled]);

  useEffect(() => {
    fetchDecomposition();
  }, [fetchDecomposition]);

  return { report, loading, fetchDecomposition };
};
